import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import PrecisionManufacturingOutlined from "@mui/icons-material/PrecisionManufacturingOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMachine, PublicState } from "../../publicSlice";
import useTranslation from "../../../shared/hooks/useTranslation";
import KioskBackButton from "../kiosk/KioskBackButton";
import { dt } from "../../../shared/themes/redesignTokens";
import publicMachinesApi from "../../api/publicMachinesApi";
import { getPublicMachineImageUrl } from "../kiosk/MachineContextHeader";
import { getMachineDisplayName, getBrandDisplayName, getMachineName, getProductionYearDisplay, getMachineTypeLabel } from "../../../shared/utils/machineUtils";
import { getReferenceLabel } from "../../../shared/utils/machineReferenceLabels";

/* ─────────────────────── Spec line helper ─────────────────────── */
interface SpecLineProps {
  label: string;
  value: string;
  mono?: boolean;
}

const SpecLine = ({ label, value, mono }: SpecLineProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: "8px",
      py: "6px",
      borderBottom: `1px solid ${dt.cardBorder}`,
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 400,
        fontFamily: dt.font,
        color: dt.textSecondary,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: mono ? dt.fontMono : dt.font,
        color: dt.textPrimary,
        letterSpacing: mono ? "0.3px" : undefined,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        textAlign: "right",
      }}
    >
      {value}
    </Box>
  </Box>
);

/* ─────────────────────── Main component ─────────────────────── */
const MachinePage = () => {
  const { machine } = useAppSelector<PublicState>((state: any) => state.public);
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");

  const { id } = useParams();

  const [isViewer, setIsViewer] = useState(false);
  const [customerName, setCustomerName] = useState<string>("");

  useEffect(() => {
    if (id) {
      dispatch(getMachine(id));
    }
  }, [id]);

  useEffect(() => {
    if (machine) {
      // Check ownership via public profile endpoint
      publicMachinesApi
        .getPublicMachine(machine.id)
        .then((profile) => {
          setIsViewer(!profile.isOwner);
          if (profile.customerName) {
            setCustomerName(profile.customerName);
          }
        })
        .catch(() => {
          setIsViewer(false);
        });
    }
  }, [machine]);

  if (!machine) {
    return null;
  }

  const imageUrl = getPublicMachineImageUrl(machine);
  const brandName = getBrandDisplayName(machine);
  const typeName = getMachineName(machine);
  const yearDisplay = getProductionYearDisplay(machine);

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        px: 2,
        pb: "80px",
      }}
    >
      {/* Page title */}
      <Box
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 2,
        }}
      >
        {translate("machineProfile")}
      </Box>

      {/* Viewer mode info banner */}
      {isViewer && customerName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            background: "rgba(52, 152, 219, 0.06)",
            border: "1px solid #AED6F1",
            borderRadius: dt.radiusSm,
            mb: 2,
          }}
        >
          <InfoOutlined sx={{ fontSize: "20px", color: dt.blueHover, flexShrink: 0 }} />
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: dt.font,
              color: dt.blueHover,
            }}
          >
            {translate("viewingMachine", { company: customerName })}
          </Box>
        </Box>
      )}

      {/* Machine image card */}
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          overflow: "hidden",
          mb: 2,
        }}
      >
        {/* Image */}
        <Box
          sx={{
            width: "100%",
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#F8FAFC",
            borderBottom: `1px solid ${dt.cardBorder}`,
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={getMachineDisplayName(machine)}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <PrecisionManufacturingOutlined
              sx={{ fontSize: "64px", color: dt.textMuted, opacity: 0.3 }}
            />
          )}
        </Box>

        {/* Name & subtitle */}
        <Box sx={{ padding: "16px" }}>
          <Box
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: dt.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {getMachineDisplayName(machine)}
          </Box>
          {typeName && (
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: dt.textSecondary,
                mt: "2px",
              }}
            >
              {typeName}
            </Box>
          )}
        </Box>
      </Box>

      {/* Specifications card */}
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          padding: "16px",
          mb: 2,
        }}
      >
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: dt.textMuted,
            mb: 1.5,
          }}
        >
          {translate("specifications")}
        </Box>

        {machine.serialNumber && (
          <SpecLine
            label={translate("serialNumber")}
            value={machine.serialNumber}
            mono
          />
        )}

        {machine.internalNumber && (
          <SpecLine
            label={translate("machineNumber")}
            value={machine.internalNumber}
            mono
          />
        )}

        {brandName && (
          <SpecLine
            label={translate("brand")}
            value={brandName}
          />
        )}

        {machine.type?.name && (
          <SpecLine
            label={translate("machineType")}
            value={getMachineTypeLabel(machine.type)}
          />
        )}

        {/* Control system / System */}
        {(machine.controlSystemBrand?.name || machine.system) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: "8px",
              py: "6px",
              borderBottom: `1px solid ${dt.cardBorder}`,
            }}
          >
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 400,
                fontFamily: dt.font,
                color: dt.textSecondary,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {translate("system")}
            </Box>
            <Box sx={{ minWidth: 0, textAlign: "right" }}>
              {machine.controlSystemBrand?.name && (
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getReferenceLabel("brand", machine.controlSystemBrand)}
                </Box>
              )}
              {machine.system && (
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: dt.fontMono,
                    color: dt.textMuted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mt: "1px",
                  }}
                >
                  {machine.system}
                  {machine.version ? ` ${machine.version}` : ""}
                </Box>
              )}
              {!machine.system && machine.version && (
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: dt.fontMono,
                    color: dt.textMuted,
                    mt: "1px",
                  }}
                >
                  {machine.version}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Operation type */}
        {machine.operationType?.name && (
          <SpecLine
            label={translate("operationType")}
            value={getReferenceLabel("operation", machine.operationType)}
          />
        )}

        {/* Axis configuration */}
        {machine.axisConfiguration?.name && (
          <SpecLine
            label={translate("axisConfig")}
            value={getReferenceLabel("axis", machine.axisConfiguration)}
          />
        )}

        {/* Production year */}
        {yearDisplay && (
          <SpecLine
            label={translate("productionYear")}
            value={yearDisplay}
          />
        )}
      </Box>

      <KioskBackButton />
    </Box>
  );
};

export default MachinePage;
